import {HDate} from '@hebcal/core'
import Month from 'src/entities/Month'

// Hebrew month constants (our storage scheme: Nissan=1, Tishrei=7, Adar=12, AdarII=13).
export const HEBREW_MONTHS = {
  NISSAN: 1,
  IYAR: 2,
  SIVAN: 3,
  TAMMUZ: 4,
  AV: 5,
  ELUL: 6,
  TISHREI: 7,
  CHESHVAN: 8,
  KISLEV: 9,
  TEVET: 10,
  SHEVAT: 11,
  ADAR: 12,
  ADAR_BET: 13
}

// UI order: Tishrei-first (civil year order, what users expect to see).
export const UI_MONTH_ORDER_REGULAR = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6]
export const UI_MONTH_ORDER_LEAP = [7, 8, 9, 10, 11, 12, 13, 1, 2, 3, 4, 5, 6]

const HEBREW_MONTH_NAMES_BASE: Record<number, string> = {
  1: 'ניסן',
  2: 'אייר',
  3: 'סיון',
  4: 'תמוז',
  5: 'אב',
  6: 'אלול',
  7: 'תשרי',
  8: 'חשוון',
  9: 'כסלו',
  10: 'טבת',
  11: 'שבט',
  12: 'אדר',
  13: "אדר ב'"
}

export const isLeapYear = (year: number): boolean => HDate.isLeapYear(year)

export const getMonthName = (month: number, year: number): string => {
  if (month === 12 && isLeapYear(year)) return "אדר א'"
  return HEBREW_MONTH_NAMES_BASE[month] ?? ''
}

// Hebrew year gimatria (e.g., 5785 → תשפ"ה). Drops the thousands prefix for compactness.
const GIMATRIA_LETTERS: Array<[number, string]> = [
  [400, 'ת'], [300, 'ש'], [200, 'ר'], [100, 'ק'],
  [90, 'צ'], [80, 'פ'], [70, 'ע'], [60, 'ס'],
  [50, 'נ'], [40, 'מ'], [30, 'ל'], [20, 'כ'], [10, 'י'],
  [9, 'ט'], [8, 'ח'], [7, 'ז'], [6, 'ו'], [5, 'ה'],
  [4, 'ד'], [3, 'ג'], [2, 'ב'], [1, 'א']
]

const convertNumberToGimatria = (n: number): string => {
  if (n <= 0) return ''
  let out = ''
  let hundreds = Math.floor(n / 100) * 100
  let remainder = n % 100
  while (hundreds > 400) {
    out += 'ת'
    hundreds -= 400
  }
  if (hundreds > 0) {
    const found = GIMATRIA_LETTERS.find(([v]) => v === hundreds)
    if (found) out += found[1]
  }
  if (remainder === 15) out += 'טו'
  else if (remainder === 16) out += 'טז'
  else {
    const tens = Math.floor(remainder / 10) * 10
    const units = remainder % 10
    if (tens > 0) {
      const f = GIMATRIA_LETTERS.find(([v]) => v === tens)
      if (f) out += f[1]
    }
    if (units > 0) {
      const f = GIMATRIA_LETTERS.find(([v]) => v === units)
      if (f) out += f[1]
    }
  }
  if (out.length >= 2) {
    return out.slice(0, -1) + '"' + out.slice(-1)
  }
  return out + "'"
}

export const getYearText = (year: number): string => {
  if (year < 1) return ''
  const millennium = Math.floor(year / 1000)
  const remainder = year % 1000
  const remainderText = convertNumberToGimatria(remainder)
  const prefix = millennium > 0 ? convertNumberToGimatria(millennium) + "'" : ''
  return prefix + remainderText
}

export const getUiOrderForYear = (year: number): number[] =>
  isLeapYear(year) ? UI_MONTH_ORDER_LEAP : UI_MONTH_ORDER_REGULAR

export const getChronologicalIndex = (month: number, year: number): number => {
  const order = getUiOrderForYear(year)
  return order.indexOf(month)
}

export const compareChronological = (m1: number, y1: number, m2: number, y2: number): number => {
  if (y1 !== y2) return y1 - y2
  return getChronologicalIndex(m1, y1) - getChronologicalIndex(m2, y2)
}

// Convert our (month, year) to @hebcal's HDate (which uses Tishrei-first: Tishrei=7 in its scheme).
// Note: @hebcal uses months.NISAN=1..TAMMUZ=4..ADAR=12 (non-leap) or ADAR_I=12, ADAR_II=13 (leap).
// This maps directly to our scheme! Both use Nissan=1.
export const addHebrewMonths = (
  month: number,
  year: number,
  delta: number
): {month: number; year: number} => {
  const hd = new HDate(1, month, year)
  const added = hd.add(delta, 'months')
  return {month: added.getMonth(), year: added.getFullYear()}
}

export const fromGregorian = (date: Date): {month: number; year: number} => {
  const hd = new HDate(date)
  return {month: hd.getMonth(), year: hd.getFullYear()}
}

// Default month: before 15th of the Hebrew month → previous; after → current.
export const getDefaultMonth = (): Month => {
  const today = new Date()
  const hd = new HDate(today)
  const day = hd.getDate()
  let month = hd.getMonth()
  let year = hd.getFullYear()
  if (day < 15) {
    const prev = addHebrewMonths(month, year, -1)
    ;({month, year} = prev)
  }
  return new Month({
    id: month,
    name: getMonthName(month, year),
    numericYear: year,
    textYear: getYearText(year)
  })
}

// Returns all months from StartingHebrewYear through (currentYear) in UI order (Tishrei first).
// Pass startingYear from config (defaults to 5785).
export const getMonthsForYearInUiOrder = (year: number): Month[] => {
  return getUiOrderForYear(year).map(
    id =>
      new Month({
        id,
        name: getMonthName(id, year),
        numericYear: year,
        textYear: getYearText(year)
      })
  )
}

// System start: data only exists from this Hebrew month/year onwards.
// Keep in sync with backend HebrewCalendar.StartingHebrewYear/Month in appsettings.json.
export const STARTING_HEBREW_YEAR = 5786
// Nisan-first scheme: 1=Nisan, 2=Iyar (default), ..., 7=Tishrei.
export const STARTING_HEBREW_MONTH = 2

// True when (month, year) is strictly before the system start.
export const isBeforeSystemStart = (month: number, year: number): boolean => {
  if (year < STARTING_HEBREW_YEAR) return true
  if (year > STARTING_HEBREW_YEAR) return false
  return compareChronological(month, year, STARTING_HEBREW_MONTH, STARTING_HEBREW_YEAR) < 0
}

// Generates available months from system start to the current Hebrew year,
// excluding future months and months before the start month in the start year. UI order.
export const getMonths = (): Month[] => {
  const result: Month[] = []
  const {month: currentMonth, year: currentYear} = fromGregorian(new Date())

  for (let year = STARTING_HEBREW_YEAR; year <= currentYear; year++) {
    for (const id of getUiOrderForYear(year)) {
      if (year === STARTING_HEBREW_YEAR && compareChronological(id, year, STARTING_HEBREW_MONTH, STARTING_HEBREW_YEAR) < 0) {
        continue
      }
      if (year === currentYear && compareChronological(id, year, currentMonth, currentYear) > 0) {
        break
      }
      result.push(
        new Month({
          id,
          name: getMonthName(id, year),
          numericYear: year,
          textYear: getYearText(year)
        })
      )
    }
  }
  return result
}

export const defaultMonth = getDefaultMonth()

// Year list for year dropdown (5785..currentYear+1).
export const getYearsForDropdown = (): number[] => {
  const {year: currentYear} = fromGregorian(new Date())
  const years: number[] = []
  for (let y = STARTING_HEBREW_YEAR; y <= currentYear + 1; y++) {
    years.push(y)
  }
  return years
}

// Back-compat exports (some callers still reference monthNames / monthsWithId / years).
export const monthNames = HEBREW_MONTH_NAMES_BASE
export const monthsWithId = Object.entries(HEBREW_MONTH_NAMES_BASE).map(([id, name]) => ({
  id: parseInt(id, 10),
  name
}))
export const years = getYearsForDropdown()
